import { useMemo } from "react";
import type { EpochSelectorItem } from "@/components/shared/EpochSelector/types";
import { EpochStatus } from "@/constants";
import { getEpochDisplayNumber } from "@/utils/campaign/epochSelector";

/**
 * Processes and filters epoch selector items for display in a searchable epoch list.
 *
 * This hook:
 * - Sorts epochs by their epoch number in ascending order
 * - Filters epochs based on search query (matches label, display number, or epoch number)
 * - Identifies the currently active epoch from the list
 * - Memoizes results to prevent unnecessary recalculations
 *
 * @param epochs - Array of epoch selector items to process
 * @param searchQuery - Search query string to filter epochs (case-insensitive)
 *
 * @returns Object containing:
 * - `sortedEpochs` - All epochs sorted by epoch number (ascending)
 * - `filteredEpochs` - Epochs matching the search query, or all sorted epochs if query is empty
 * - `currentEpoch` - The currently active epoch, or undefined if none are active
 *
 * @example
 * ```tsx
 * const [searchQuery, setSearchQuery] = useState("");
 * const { filteredEpochs, currentEpoch } = useEpochSelectorData(epochItems, searchQuery);
 *
 * return (
 *   <>
 *     <SearchInput value={searchQuery} onChange={setSearchQuery} />
 *     <EpochList epochs={filteredEpochs} currentEpoch={currentEpoch} />
 *   </>
 * );
 * ```
 */
export const useEpochSelectorData = (epochs: EpochSelectorItem[], searchQuery: string) => {
  const sortedEpochs = useMemo(() => {
    return [...epochs].sort((a, b) => a.epochNumber - b.epochNumber);
  }, [epochs]);

  const filteredEpochs = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return sortedEpochs;

    return sortedEpochs.filter((epoch) => {
      const displayNumber = getEpochDisplayNumber(epoch);
      const label = epoch.label?.toLowerCase() ?? "";
      const epochNumber = epoch.epochNumber + 1;

      return (
        label.includes(normalizedQuery) ||
        (displayNumber?.toString() || "").includes(normalizedQuery) ||
        epochNumber.toString().includes(normalizedQuery)
      );
    });
  }, [sortedEpochs, searchQuery]);

  const currentEpoch = useMemo(
    () => sortedEpochs.find((epoch) => epoch.status === EpochStatus.ACTIVE || epoch.status === EpochStatus.PAUSED),
    [sortedEpochs],
  );

  return { sortedEpochs, filteredEpochs, currentEpoch };
};
